import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEnvelope, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import BrokerageStatusChecker from './BrokerageStatusChecker';
import axiosInstance from '../../helpers/api';
import './AddAgentModal.css';

interface AddAgentModalProps {
  show: boolean;
  onHide: () => void;
  brokerageId: string | number | null | undefined;
  onAgentAdded: () => void;
}

interface NewAgentData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  licenseNumber: string;
  licenseType: 'BR' | 'SA';
  plan: 'essential'; // Always default to essential - agents choose their own upgrades
  isAdmin: boolean;
}

const AddAgentModal: React.FC<AddAgentModalProps> = ({ show, onHide, brokerageId, onAgentAdded }) => {
  const [formData, setFormData] = useState<NewAgentData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    licenseNumber: '',
    licenseType: 'SA',
    plan: 'essential',
    isAdmin: false
  });

  const [validationErrors, setValidationErrors] = useState<Partial<NewAgentData>>({});
  const [apiError, setApiError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [savedMemberId, setSavedMemberId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [currentStep, setCurrentStep] = useState<'form' | 'invite'>('form');

  // Validate form data
  const validateForm = (): boolean => {
    const errors: Partial<NewAgentData> = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Invalid phone number format';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Format phone number
  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return value;
  };

  // Handle input changes
  const handleInputChange = (field: keyof NewAgentData, value: string) => {
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Save agent to database
  const handleSaveAgent = async () => {
    if (!validateForm()) return;

    // Convert brokerageId to string and validate before making the request
    const brokerageIdStr = String(brokerageId || '');
    if (!brokerageIdStr || brokerageIdStr.trim() === '') {
      setApiError('No brokerage ID found. Please ensure you are properly logged in to a brokerage account.');
      return;
    }

    setIsSubmitting(true);
    setApiError('');
    setSuccessMessage('');

    try {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      console.log('🔍 [DEBUG] Attempting to save agent with brokerage ID:', brokerageIdStr);

      const response = await axiosInstance.post('/broker/invitations/save-agent', {
        agentData: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone,
          licenseNumber: formData.licenseNumber.trim(),
          licenseType: formData.licenseType,
          plan: formData.plan,
          isAdmin: formData.isAdmin
        },
        brokerageId: brokerageIdStr.trim()
      }, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      const result = response.data;

      if (!result.success) {
        // Enhanced error handling for brokerage validation
        if (result.error && result.error.includes('Brokerage with ID')) {
          const debugInfo = result.debug ? 
            `\n\nDebug Info:\n- Requested ID: ${result.debug.requestedId}\n- Available brokerages: ${result.debug.availableBrokerages?.length || 0}` : '';
          
          throw new Error(`Brokerage Setup Required: The brokerage (ID: ${brokerageIdStr}) was not found in the system. Please ensure your brokerage profile is properly set up before adding agents.${debugInfo}`);
        }
        
        throw new Error(result.error || 'Failed to save agent');
      }

      setSavedMemberId(result.member.id);
      setSuccessMessage(`${formData.firstName} ${formData.lastName} has been added to your team!`);
      setCurrentStep('invite');
    } catch (error: any) {
      console.error('❌ [ERROR] Failed to save agent:', error);
      // Handle axios errors
      if (error.response) {
        // Server responded with error
        const errorData = error.response.data;
        setApiError(errorData.error || errorData.message || 'Failed to save agent. Please try again.');
      } else if (error.request) {
        // Request made but no response
        setApiError('Cannot connect to server. Please check your connection.');
      } else {
        // Other errors
        setApiError(error.message || 'An error occurred while saving the agent');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Send invitation email
  const handleSendInvitation = async () => {
    if (!savedMemberId) return;

    setIsSendingInvite(true);
    setApiError('');

    try {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await axiosInstance.post('/broker/invitations/send-invitation', {
        memberId: savedMemberId
      }, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'Failed to send invitation');
      }

      setSuccessMessage(`Invitation sent to ${formData.email}!`);
      
      // Reset and close after success
      setTimeout(() => {
        handleReset();
        onAgentAdded();
      }, 2000);
    } catch (error: any) {
      console.error('❌ [ERROR] Failed to send invitation:', error);
      // Handle axios errors
      if (error.response) {
        // Server responded with error
        const errorData = error.response.data;
        setApiError(errorData.error || errorData.message || 'Failed to send invitation. Please try again.');
      } else if (error.request) {
        // Request made but no response
        setApiError('Cannot connect to server. Please check your connection.');
      } else {
        // Other errors
        setApiError(error.message || 'An error occurred while sending the invitation');
      }
    } finally {
      setIsSendingInvite(false);
    }
  };

  // Skip invitation and close
  const handleSkipInvitation = () => {
    handleReset();
    onAgentAdded();
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      licenseNumber: '',
      licenseType: 'SA',
      plan: 'essential',
      isAdmin: false
    });
    setValidationErrors({});
    setApiError('');
    setSuccessMessage('');
    setSavedMemberId(null);
    setCurrentStep('form');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleReset} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faPlus} className="me-2 text-primary" />
          Add New Agent
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Brokerage Status Check */}
        <BrokerageStatusChecker 
          brokerageId={String(brokerageId || '')}
          onSetupRequired={() => {
            // You can add navigation to brokerage setup here
            console.log('Navigate to brokerage setup');
          }}
        />

        {apiError && (
          <Alert variant="danger" dismissible onClose={() => setApiError('')}>
            <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
            {apiError}
          </Alert>
        )}
        
        {successMessage && (
          <Alert variant="success">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            {successMessage}
          </Alert>
        )}

        {currentStep === 'form' && (
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    First Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    isInvalid={!!validationErrors.firstName}
                    placeholder="Enter first name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Last Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    isInvalid={!!validationErrors.lastName}
                    placeholder="Enter last name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Email Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    isInvalid={!!validationErrors.email}
                    placeholder="agent@example.com"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Phone Number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    isInvalid={!!validationErrors.phone}
                    placeholder="(555) 123-4567"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>License Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.licenseNumber}
                    onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                    placeholder="Optional"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>License Type</Form.Label>
                  <Form.Select
                    value={formData.licenseType}
                    onChange={(e) => handleInputChange('licenseType', e.target.value)}
                  >
                    <option value="SA">Sales Agent (SA)</option>
                    <option value="BR">Broker (BR)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="isAdmin"
                    label={
                      <span>
                        <strong>Grant Admin Privileges</strong>
                        <br />
                        <small className="text-muted">
                          Admins can manage team members, view billing, and access all broker features
                        </small>
                      </span>
                    }
                    checked={formData.isAdmin}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAdmin: e.target.checked }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Alert variant="info" className="mt-3">
              <small>
                <strong>Note:</strong> All agents start with the Essential plan. 
                They can upgrade to Professional, Deal AI, or Agent Kairos after accepting your invitation.
              </small>
            </Alert>
          </Form>
        )}

        {currentStep === 'invite' && (
          <div className="text-center py-4">
            <FontAwesomeIcon icon={faCheckCircle} className="text-success mb-3" size="3x" />
            <h5 className="mb-3">Agent Added Successfully!</h5>
            <p className="text-muted mb-4">
              Would you like to send an invitation email to {formData.firstName} {formData.lastName}?
            </p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {currentStep === 'form' && (
          <>
            <Button variant="secondary" onClick={handleReset}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveAgent}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Save Agent
                </>
              )}
            </Button>
          </>
        )}

        {currentStep === 'invite' && (
          <>
            <Button variant="secondary" onClick={handleSkipInvitation}>
              Skip Invitation
            </Button>
            <Button 
              variant="success" 
              onClick={handleSendInvitation}
              disabled={isSendingInvite}
            >
              {isSendingInvite ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Sending...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddAgentModal;